import React, { useEffect } from "react";
import { useSelector } from 'react-redux';
import Clarity from './Clarity';
import { isEmbeddedInIframe } from './embedded';
import { isProduction, isTokenInUrl, startClarityForLexon, stopClarityForLexon } from './lexonClarity';

// PEC-1557: espera máxima (250 ms x 120 = 30 s) a que el token de entrada de Lexon salga de la URL.
const LEXON_TOKEN_WAIT_INTERVAL_MS = 250;
const LEXON_TOKEN_WAIT_MAX_ATTEMPTS = 120;

const CookieBot = () => {
  const domainGroupId = 'bb0d601e-f391-4331-aee8-dc7e51d1538b';
  const clarityId = 'pyen9cwnnz';
  const isIframe = isEmbeddedInIframe();

  // PEC-1557: embebido en Lexon no hay banner de Cookiebot. El consentimiento llega en el token
  // de entrada (claim accept_cookies, normalizado en currentUser por UserCalendar / UserLexon) y la
  // grabación solo se activa en PRO, tras la redirección que saca el token de la URL, con
  // consentimiento solo analítica y etiqueta user_type para filtrar al grupo (ver lexonClarity.js).
  const acceptCookiesFromLexon = useSelector(state => state.currentUser?.accept_cookies);
  const idUser = useSelector(state => state.currentUser?.idUser);
  const idCompany = useSelector(state => state.currentUser?.idCompany);
  const idClienteNavision = useSelector(state => state.currentUser?.idClienteNavision);
  const account = useSelector(state => state.lexon?.account);

  useEffect(() => {
    if (!isIframe) return undefined;
    if (acceptCookiesFromLexon === false && Clarity.hasStarted()) {
      console.log('[clarity] token de Lexon sin consentimiento de cookies: se detiene la grabación');
      stopClarityForLexon();
      return undefined;
    }
    if (acceptCookiesFromLexon !== true || Clarity.hasStarted()) return undefined;
    if (!isProduction()) {
      console.log('[clarity] iframe de Lexon: la grabación solo se activa en PRO');
      return undefined;
    }
    const user = { idUser, idCompany, idClienteNavision, account };
    // Devuelve true cuando ya no hay que seguir esperando (arrancado o intentado), false si el
    // token de entrada sigue en la URL y hay que esperar a la redirección a /calendar.
    const tryStart = () => {
      if (Clarity.hasStarted()) return true;
      if (isTokenInUrl()) return false;
      startClarityForLexon({ init: () => Clarity.init(clarityId), user });
      return true;
    };
    if (tryStart()) return undefined;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (tryStart() || attempts >= LEXON_TOKEN_WAIT_MAX_ATTEMPTS) clearInterval(timer);
    }, LEXON_TOKEN_WAIT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isIframe, acceptCookiesFromLexon, idUser, idCompany, idClienteNavision, account, clarityId]);

  useEffect(() => {
    window.addEventListener('CookiebotOnConsentReady', () => {
      try {
        if (
          typeof window.Cookiebot !== 'undefined' &&
          window.Cookiebot?.consent
        ) {
          const hasConsentForAnalytics = window.Cookiebot.consent.marketing || window.Cookiebot.consent.statistics;

          if (hasConsentForAnalytics && !isIframe && !Clarity.hasStarted()) {
            Clarity.init(clarityId);

            setTimeout(() => {
              if (window.clarity) {
                window.clarity("consent", true);
                console.log('Consent signal sent to Microsoft Clarity');
              }
            }, 100);
          } else if (!hasConsentForAnalytics) {
            console.log('User did not consent to analytics cookies - Clarity will not be initialized');
          }
        }
      } catch (error) {
               console.error("Error in CookiebotOnConsentReady event", error);
      }
    }, false);

    window.addEventListener('CookiebotOnConsentChanged', () => {
      try {
        if (
          typeof window.Cookiebot !== 'undefined' &&
          window.Cookiebot?.consent
        ) {
          const hasConsentForAnalytics = window.Cookiebot.consent.marketing || window.Cookiebot.consent.statistics;

          if (hasConsentForAnalytics && !isIframe && !Clarity.hasStarted()) {
            Clarity.init(clarityId);

            setTimeout(() => {
              if (window.clarity) {
                window.clarity("consent", true);
                console.log('Consent signal sent to Microsoft Clarity after consent change');
              }
            }, 100);
          } else if (!hasConsentForAnalytics && Clarity.hasStarted()) {
            console.log('User revoked consent - stopping Clarity');
            if (window.clarity) {
              window.clarity("consent", false);
              window.clarity("stop");
            }
          }
        }
      } catch (error) {
        console.error("Error in CookiebotOnConsentChanged event", error);
      }
    }, false);
  }, []);

  if (typeof window === "undefined") {
    return null;
  }

  if (!domainGroupId || !document) {
    console.error("Cookie bot domain group id is not defined! or not document defined");
    return null;
  }

  if (domainGroupId.length !== 36) {
    console.warn(
      `The cookie bot domain group id is ${domainGroupId.length} characters, instead it should be 36 characters!`
    );
  }

  const existingScript = document.getElementById("CookieBot");
  if (!existingScript && !isIframe) {
    const script = document.createElement("script");
    script.setAttribute("id", "CookieBot");
    script.setAttribute("src", "https://consent.cookiebot.com/uc.js");
    script.setAttribute("data-cbid", domainGroupId);
    script.setAttribute("data-blockingmode", "manual");
    script.setAttribute("type", "text/javascript");
    const head = document.querySelector("html > head");
    head.insertBefore(script, head.firstChild);

    // Después de insertar el script de CookieBot, añadir script de Lefebvre solo en producción
    if (window.REACT_APP_ENVIRONMENT === 'PRO') {
      const existingLefebvreScript = document.getElementById("LefebvreCookieScript");
      if (!existingLefebvreScript) {
        const lefebvreScript = document.createElement("script");
        lefebvreScript.setAttribute("id", "LefebvreCookieScript");
        lefebvreScript.setAttribute("defer", "");
        lefebvreScript.setAttribute("src", `https://atenea.lefebvre.es/cookiebot-script?url=${window.location.origin}`);
        head.insertBefore(lefebvreScript, script.nextSibling);
      }
    }
  }

  return <></>;
};

export default CookieBot;
